import { useTranslation } from 'react-i18next';
import { formatUsd } from '@/shared/utils/currency';
import { formatDate } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';
import { TriangleAlertIcon } from '@/shared/ui/icons';
import { computePinflLimitView } from '@/entities/pinfl';
import type { ApiPinflGetResponse } from '@/shared/types/api/pinfl';

type LimitTone = 'positive' | 'neutral' | 'danger';

type Props = {
  limit: ApiPinflGetResponse | null;
  currentDeclared: number;
  isLoading?: boolean;
};

const toneFor = (remaining: number): LimitTone => {
  if (remaining > 0) return 'positive';
  if (remaining === 0) return 'neutral';
  return 'danger';
};

const TONE_BAR: Record<LimitTone, string> = {
  positive: 'bg-brand-600',
  neutral: 'bg-border-strong',
  danger: 'bg-danger',
};

export const FeaturesDeclarationPinflLimitBar = ({ limit, currentDeclared, isLoading }: Props) => {
  const { t } = useTranslation('declaration');
  if (!limit) {
    return (
      <div className="sticky top-0 z-10 border-b border-border bg-surface p-4 shadow-card">
        <p className="text-sm text-ink-muted">
          {isLoading ? t('limitLoading') : t('limitNotFound')}
        </p>
      </div>
    );
  }

  // Yuridik shaxs: backend signals no spending limit applies. Replace the
  // bar with a single explanatory line so the user knows usage isn't tracked.
  if (limit.limit_applies === false) {
    return (
      <div className="sticky top-0 z-10 border-b border-border bg-surface p-4 shadow-card">
        <p className="text-sm font-medium text-ink-soft">{t('noLimitLegal')}</p>
      </div>
    );
  }

  // Ceiling from the dynamic API `limit`; used includes the in-progress
  // declaration. This is the value we display (used over ceiling), not the
  // remaining amount.
  const { used, ceiling, isOver } = computePinflLimitView(limit, currentDeclared);
  // Internal-only: drives the progress-bar colour (not shown as a number).
  const tone = toneFor(ceiling - used);

  const percent = ceiling > 0 ? Math.max(0, Math.min(100, (used / ceiling) * 100)) : 0;

  return (
    <div
      className={cn(
        'sticky top-0 z-10 border-b bg-surface p-4 shadow-card',
        isOver ? 'border-danger/40 bg-danger/5' : 'border-border',
      )}
    >
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 font-semibold text-ink-soft">
          {isOver && <TriangleAlertIcon className="h-4 w-4 text-danger" />}
          {t('limitTitle')}
        </span>
        <span className={cn('font-semibold', isOver ? 'text-danger' : 'text-ink-soft')}>
          {formatUsd(used)} / {formatUsd(ceiling)}
        </span>
      </div>

      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface-muted">
        <div
          className={cn('h-full transition-all', TONE_BAR[tone])}
          style={{ width: `${percent}%` }}
        />
      </div>

      {isOver && (
        <div className="mt-2 flex items-center gap-1.5 rounded-md bg-danger/10 px-2.5 py-1.5 text-xs font-semibold text-danger">
          <TriangleAlertIcon className="h-4 w-4 shrink-0" />
          <span>{t('notEnough')}</span>
        </div>
      )}

      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-ink-muted">
        <div>{t('used', { amount: formatUsd(limit.used_amount) })}</div>
        <div>{t('now', { amount: formatUsd(currentDeclared) })}</div>
      </div>

      <p className="mt-2 text-[11px] text-ink-faint">
        {t('resetHint', { date: formatDate(limit.next_reset_at) })}
      </p>
    </div>
  );
};
